"use client";

/* 地址选择组件-用户地址列表 */
// React
import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
// 三方库/框架
import { useTranslation } from "@/i18n/client";
import to from "await-to-js";
// 公共方法
import { initAddress, getGeolocationAddressInfo } from "@/utils/addressUtils";
import { queryGlobalCountriesData } from "@/utils/countryUtils";
import JDILogin from "@/app/login/index.login";
// 接口
import { Address } from "@/apis/address/address.interface";
import { getUserAddressList, queryPostCode } from "@/apis/address/address.api";
// 组件
import { Radio, FormControlLabel, Typography, Divider, TextField, InputAdornment, IconButton, Button, CircularProgress } from "@mui/material";
import UKLoadingIcon from "@/components/UKLoadingIcon";
import UKToast from "@/components/UKToast";
import SiteChangeConfirmDialog from "@/components/countrySelect/SiteChangeConfirmDialog";
// 资源
import AddressClearIcon from "@/assets/icons/address/AddressClearIcon.svg";
// 枚举
import { ADDRESS_VERIFY_TYPE } from "@/constants/address";
// 样式
import styles from "./index.module.scss";

interface DeliveryListProps {
  selectAddressId?: string | number; // 默认的地址id
}

export interface DeliveryListRef {
  refreshAddressList: () => Promise<void>; // 刷新列表
  getSelectAddressInfo: () => Promise<Address>; // 获取选择的地址信息
  clearSelectAddress: () => void; // 清除当前勾选内容
}

interface DeliveryListProps {
  selectAddressId?: string | number; // 默认的地址id
  onApply: (addressInfo:  Address | null) => void; // 确认按钮点击事件
}

const DeliveryList = forwardRef<DeliveryListRef, DeliveryListProps>((props, ref) => {
  const { selectAddressId, onApply } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [locationLoading, setLocationLoading] = useState(false);
  const [postCodeValidating, setPostCodeValidating] = useState(false); // postcode校验中
  const [selectType, setSelectType] = useState<"useCurrentLocation" | "postCode" | "userAddress" | "">(""); // 当前选中的地址类型
  const [selectAddress, setSelectAddress] = useState<Address | null>(null); // 选中的地址信息
  const [addressList, setAddressList] = useState<Address[]>([]); // 常规地址分堆
  const [unSyncAddressList, setUnSyncAddressList] = useState<Address[] | null>(null); // 草稿地址分堆
  const [unavailableAddressList, setUnavailableAddressList] = useState<Address[] | null>(null); // 不在配送范围的地址分堆
  const [unSupportedSiteAddressList, setUnSupportedSiteAddressList] = useState<Address[] | null>(null); // 跨站点地址分堆

  const [inputPostcode, setInputPostcode] = useState(""); // 邮编输入
  const [showSiteChangeDialog, setShowSiteChangeDialog] = useState(false); // 控制跨站点确认弹窗显示
  const [otherSiteAddressInfo, setOtherSiteAddressInfo] = useState<Address | null>(null);
  const [optionChangeInProgress, setOptionChangeInProgress] = useState(false);
  
  useImperativeHandle(ref, () => ({
    refreshAddressList: fetchAddressList,
    getSelectAddressInfo,
    clearSelectAddress
  }));

  // 获取地址列表数据
  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const { isLoggedIn } = await JDILogin.getInstance().getUserInfo();
    if (isLoggedIn) {
      fetchAddressList();
    } else {
      setLoading(false);
    }
  }

  const getSelectAddressInfo = () => {
    return new Promise<Address>((resolve, reject) => {
      if (!selectAddress) {
        reject(t("address.noAddressSelected"));
      } else if (!selectAddress.postCode) {
        reject(t("address.noPostCode"));
      } else if (selectAddress.isDeliverable === false) {
        reject(t("address.notDeliverable"));
      } else {
        resolve(selectAddress);
      }
    });
  }

  const clearSelectAddress = () => {
    setSelectAddress(null);
    setSelectType("");
    setInputPostcode("");
  }

  const checkPostCode = async (postCode: string) => {
    if(!postCode) return;
    setPostCodeValidating(true);
    const postCodeValue = postCode.replace(/\s+/g, "");
    const { currentCountry } = await queryGlobalCountriesData();
    const [, result] = await to(queryPostCode({ 
      postCode: postCodeValue ? postCodeValue.toUpperCase() : postCodeValue, 
      checkDeliverableFlag: true,
      countryId: currentCountry?.countryId,
      countryCode: currentCountry?.countryCode
    }));
    setSelectAddress(result?.code === "200" && result?.data ? result.data : {errorMsg: result?.msg} as Address);
    setPostCodeValidating(false);
  }


  const handleOptionChange = async (option: "currentLocation" | "postcode" | Address) => {
    if (optionChangeInProgress) {
      return;
    }
    
    setOptionChangeInProgress(true);
    
    try {
      if (option === "currentLocation") {
        setLocationLoading(true);
        const [, res] = await to(getGeolocationAddressInfo({
          verifyType: ADDRESS_VERIFY_TYPE.SITE_VERIFY // 站点维度校验
        }));
        if (res?.addressInfo) {
          setSelectType("useCurrentLocation");
          setInputPostcode("");
          setSelectAddress({
            ...res.addressInfo,
            isDeliverable: !(res?.addressInfo?.verifyResult === false)
          });
        } else {
          UKToast.show({
            content: t("address.notDeliverable") as any,
            icon: "error",
          });
        }
        setLocationLoading(false);
      } else if (option === "postcode") {
        setSelectType("postCode");
        setSelectAddress(null);
      } else {
        if (option?.otherSite) {
          setOtherSiteAddressInfo(option);
          // 跨站点二次确认
          setShowSiteChangeDialog(true);
        } else {
          setSelectType("userAddress");
          setInputPostcode("");
          setSelectAddress(option);
        }
      }
    } finally {
      setOptionChangeInProgress(false);
    }
  }

  const handleApply = async () => {
    if (selectType === "postCode" && postCodeValidating) {
      // 等待校验完成后再继续
      await new Promise<void>((resolve) => {
        const interval = setInterval(() => {
          if (!postCodeValidating) {
            clearInterval(interval);
            resolve();
          }
        }, 50);
      });
    }
    getSelectAddressInfo()
      .then((res) => {
        onApply?.(res);
      })
      .catch((err) => {
        UKToast.show({
          content: err,
          icon: "error",
        });
      });
  };
  
  // 获取地址列表
  const fetchAddressList = async () => {
    setLoading(true);
    
    // 初始化位置信息
    const [, locationData] = await to<Address | null>(initAddress());
    
    // 获取地址列表
    const params = {
      source: 2,
      deliverPile: true,
    };
    const [, result] = await to(getUserAddressList(params));
    
    const { data } = result as any;
    const list = data?.addressList as Address[] || [];
    const otherSiteList = (data?.unSupportedSiteAddressList as Address[] || [])?.map(item => {
      return {
        ...item,
        otherSite: true // 跨站点地址
      }
    });
    const unavailableList = (data?.undeliverableAddressList as Address[] || [])?.map(item => {
      return {
        ...item,
        disableSelection: true // 不可选
      }
    });
    const draftList = data?.unSyncAddressList as Address[] || [];
    
    // 优先使用传入的selectAddressId 其次使用缓存中的addressId
    const addressIdToMatch = selectAddressId ?? locationData?.addressId;
    
    // 查找并处理匹配的地址
    const { foundAddress, updatedLists } = findAndProcessAddress(
      addressIdToMatch, 
      [list, unavailableList, otherSiteList, draftList]
    );

    setLoading(false);
    
    // 设置地址列表数据
    setAddressList(updatedLists[0]);
    setUnavailableAddressList(updatedLists[1]);
    setUnSupportedSiteAddressList(updatedLists[2]);
    setUnSyncAddressList(updatedLists[3]);

    if (!addressIdToMatch && locationData) {
      // 没有地址id 勾选postCode
      setSelectType("postCode");
      setInputPostcode(locationData?.postCode);
      setSelectAddress(locationData);
    }else{
      setSelectAddress(foundAddress || updatedLists?.[0]?.[0] || null);
    }
  };
  
  // 查找并处理地址列表
  const findAndProcessAddress = (
    addressIdToMatch: string | number | undefined, 
    lists: (Address[] | null)[]
  ): { 
    foundAddress: Address | undefined; 
    updatedLists: Address[][] 
  } => {
    // 如果没有要匹配的ID，直接返回原始列表
    if (!addressIdToMatch) {
      return { 
        foundAddress: undefined, 
        updatedLists: lists.map(list => list || []) as Address[][] 
      };
    }
    
    let foundAddress: Address | undefined;
    let foundInList: number = -1;
    
    // 在可用列表+不可用列表+草稿列表的集合中查找匹配的地址
    for (let i = 0; i < lists.length; i++) {
      const currentList = lists[i];
      if (!currentList?.length) continue;
      
      const index = currentList.findIndex(addr => addr.addressId === addressIdToMatch);
      if (index !== -1) {
        foundAddress = currentList[index];
        foundInList = i;
        break;
      }
    }
    
    const updatedLists = lists.map(list => list ? [...list] : []) as Address[][];
    
    // 如果找到了匹配的地址，将其移到对应列表的顶部
    if (foundAddress && foundInList !== -1) {
      updatedLists[foundInList] = updatedLists[foundInList].filter(
        addr => addr.addressId !== addressIdToMatch
      );
      updatedLists[foundInList] = [foundAddress, ...updatedLists[foundInList]];
    }
    
    return { foundAddress, updatedLists };
  };

  // 判断是否是整个列表的最后一项
  const isLastItemInAllLists = (address: Address) => {
    let lastNonEmptyList: Address[] | null = null;
    
    // 实际渲染顺序从后向前检查
    if (unSyncAddressList && unSyncAddressList.length > 0) {
      lastNonEmptyList = unSyncAddressList;
    } else if (unSupportedSiteAddressList && unSupportedSiteAddressList.length > 0) {
      lastNonEmptyList = unSupportedSiteAddressList;
    } else if (unavailableAddressList && unavailableAddressList.length > 0) {
      lastNonEmptyList = unavailableAddressList;
    } else if (addressList && addressList.length > 0) {
      lastNonEmptyList = addressList;
    }
    if (!lastNonEmptyList) return false;
    
    return lastNonEmptyList[lastNonEmptyList.length - 1].addressId === address.addressId;
  };

  const renderAddressItem = (address: Address, mode: "normal" | "unavailable" | "otherSite" | "draft") => {
    const isLastItem = isLastItemInAllLists(address);

    const isUnavailable = mode === "unavailable" || mode === "otherSite";
    const disableSelection = address?.disableSelection;
    return (
      <React.Fragment key={address.addressId}>
        <div 
          className={`${styles.addressItem} ${isUnavailable ? styles.unavailable : ""}`}
          onClick={() => !disableSelection && handleOptionChange(address)}
        >
          {mode !== "draft" && (
            <FormControlLabel
              value={address.addressId.toString()}
              control={
                <Radio 
                  checked={selectAddress?.addressId === address.addressId}
                  disabled={disableSelection}
                  color="secondary"
                  sx={{
                    color: "#E0E1E5",
                    "&.Mui-checked": {
                      color: "#CC0C1C",
                    },
                  }}
                />
              }
              label={
                <div className={styles.addressContent}>
                  <div className={styles.addressHeader}>
                    <div className={styles.contactName}>{address.name || ""}</div>
                    <div className={styles.contactTel}>
                      {address.areaCode || address.mobile
                      ? `${address.areaCode ? `+${address.areaCode}` : ""} ${address.mobile || ""}`
                      : ""}
                    </div>
                  </div>
      
                  <div className={styles.addressDetail}>
                    {[
                      address.addressDetail,
                      address.townName,
                      address.countyName,
                      address.cityName,
                      address.provinceName,
                      address.countryName
                    ].filter(Boolean).join(", ")}
                  </div>
                  <div className={styles.postCode}>{address.postCode || ""}</div>
                </div>
              }
              className={styles.radioControl}
            />
          )}
        </div>

        {!isLastItem && <Divider className={styles.divider} />}
      </React.Fragment>
    );
  };

  // 常规地址列表
  const renderNormalAddressList = () => {
    if (!addressList || addressList.length === 0) return null;

    return (
      <div>
        <div className={styles.sectionTitle}>{t("address.myAddress")}</div>
        {addressList.map((address) => renderAddressItem(address, "normal"))}
      </div>
    );
  };

  // 不在配送范围的地址列表
  const renderUnavailableAddressList = () => {
    if (!unavailableAddressList || unavailableAddressList.length === 0) return null;

    return (
      <div>
        <div className={`${styles.sectionTitle} ${styles.disabled}`}>{t("address.unavailableListTitle")}</div>
        <div style={{ opacity: "0.4" }}>
          {unavailableAddressList.map((address) => renderAddressItem(address, "unavailable"))}
        </div>
      </div>
    );
  };

  // 跨站点地址列表
  const renderUnSupportedSiteAddressList = () => {
    if (!unSupportedSiteAddressList || unSupportedSiteAddressList.length === 0) return null;

    return (
      <div>
        <div className={`${styles.sectionTitle} ${styles.disabled}`}>{t("address.otherRegionsAddresses")}</div>
        <div style={{ opacity: "0.4" }}>
          {unSupportedSiteAddressList.map((address) => renderAddressItem(address, "otherSite"))}
        </div>
      </div>
    );
  };

  // 草稿地址列表
  const renderUnSyncAddressList = () => {
    if (!unSyncAddressList || unSyncAddressList.length === 0) return null;

    return (
      <div>
        <div className={`${styles.sectionTitle} ${styles.disabled}`}>{t("address.draftAddresses")}</div>
        <div style={{ opacity: "0.4" }}>
          {unSyncAddressList.map((address) => renderAddressItem(address, "draft"))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.deliveryList}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <UKLoadingIcon type="black" />
        </div>
      ) : (
        <>
          <div className={styles.locationOptions}>
            <FormControlLabel
              control={
                <Radio
                  checked={selectType === "useCurrentLocation"}
                  color="secondary"
                  sx={{
                    color: "#E0E1E5",
                    "&.Mui-checked": {
                      color: "#CC0C1C",
                    },
                  }}
                />
              }
              label={
                <div className={styles.radioLabel}>
                  {t("address.useMyLocation")}
                  {locationLoading ? (
                    <UKLoadingIcon type="black" className={styles.locationLoading} />
                  ) : (
                    selectType === "useCurrentLocation" && (<span className={styles.postCodeLabel}>{selectAddress?.postCode || ""}</span>)
                  )}
                </div>
              }
              onClick={() => handleOptionChange("currentLocation")}
            />
            {(selectType === "useCurrentLocation" && selectAddress?.isDeliverable === false) && <div className={styles.warningMessage}>{t("address.locationUnableTips")}</div>}

            <FormControlLabel
              className={styles.postcodeFormControl}
              control={
                <Radio
                  checked={selectType === "postCode"}
                  color="secondary"
                  sx={{
                    color: "#E0E1E5",
                    "&.Mui-checked": {
                      color: "#CC0C1C",
                    },
                  }}
                />
              }
              label={
                <div className={styles.postcodeInput}>
                  <span className={styles.radioLabel}>{t("address.postcode")}</span>
                  <TextField
                    value={inputPostcode}
                    variant="outlined"
                    size="small"
                    error={!!selectAddress?.errorMsg}
                    sx={{
                      flexGrow: 1
                    }}
                    placeholder={t("address.enterPostcode")}
                    slotProps={{
                      input: {
                        autoComplete: "off",
                        endAdornment: (
                          <InputAdornment position="end">
                            {postCodeValidating ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : inputPostcode ? (
                              <IconButton
                                onClick={() => {
                                  setInputPostcode("");
                                }}
                              >
                                <AddressClearIcon />
                              </IconButton>
                            ) : null}
                          </InputAdornment>
                        ),
                      },
                      htmlInput: {
                        maxLength: 10,
                        minLength: 3,
                      },
                    }}
                    onBlur={() => checkPostCode(inputPostcode)}
                    onChange={(e) => setInputPostcode(e.target.value)}
                  />
                </div>
              }
              onClick={() => handleOptionChange("postcode")}
            />
            {selectAddress?.errorMsg && (<div className={styles.errorMessage}>{selectAddress.errorMsg}</div>)}
            {(selectType === "postCode" && selectAddress?.isDeliverable === false) && <div className={styles.warningMessage}>{t("address.deliveryTips")}</div>}
          </div>

          {/* 常规地址列表 */}
          {addressList && addressList.length > 0 ? (
            renderNormalAddressList()
          ) : null}
          
          {/* 不在配送范围的地址列表 */}
          {unavailableAddressList && unavailableAddressList.length > 0 ? (
            renderUnavailableAddressList()
          ) : null}
          
          {/* 跨站点地址列表 */}
          {unSupportedSiteAddressList && unSupportedSiteAddressList.length > 0 ? (
            renderUnSupportedSiteAddressList()
          ) : null}
          
          {/* 草稿地址列表 */}
          {unSyncAddressList && unSyncAddressList.length > 0 ? (
            renderUnSyncAddressList()
          ) : null}
          
          {/* 空数据 */}
          {(!addressList || addressList.length === 0) && 
          (!unSyncAddressList || unSyncAddressList.length === 0) && 
          (!unavailableAddressList || unavailableAddressList.length === 0) &&
          (!unSupportedSiteAddressList || unSupportedSiteAddressList.length === 0) && (
            <div className={styles.emptyList}>
              <Typography variant="body2" color="textSecondary" align="center">
                {t("common.pageStatus.nothing")}
              </Typography>
            </div>
          )}
          
          {/* 底部按钮 */}
          <div className={styles.bottomBarWrap}>
            <Button 
              className={styles.applyButton}
              onClick={handleApply}
            >
              {t("address.lib.apply")}
            </Button>
          </div>
        </>
      )}

      {/* 跨站点确认弹窗 */}
      <SiteChangeConfirmDialog
        open={showSiteChangeDialog}
        toCountryCode={otherSiteAddressInfo?.countryCode || ""}
        toCountryName={otherSiteAddressInfo?.countryNameMultiLan || otherSiteAddressInfo?.countryName || ""}
        setDialogOpen={setShowSiteChangeDialog}
      />
    </div>
  );
});

DeliveryList.displayName = "DeliveryList";

export default DeliveryList;
